import { StringUtil, UrlUtil, Alignment } from "@mcleod/core";
import { LayoutProvidedReportDecoratorProps, LookupModelSearchEvent, Snackbar, Layout, ReportDecorator, SlideoutDecorator } from "@mcleod/components";
import { AutogenLayoutInvoicesDueReminderReport } from "./autogen/AutogenLayoutInvoicesDueReminderReport";
import { ModelCustomer } from "../models/ModelCustomer";

export class InvoicesDueReminderReport extends AutogenLayoutInvoicesDueReminderReport {
    customerId: string | null = null;
    customerIds: string[] = [];
    recipientInfoVisible: boolean = false;

    public override get reportDecoratorProps(): Partial<LayoutProvidedReportDecoratorProps> {
        return {
            reportPreviewTitle: "Invoices Due Reminder Report"
        };
    }

    override onLoad(): void | Promise<void> {
        this.textboxDueInDays.text = "1";
        this.textboxEditSubject.text = "Invoices Due Reminder";

        const customerId = UrlUtil.getSearchParamFromUrl("customer_id");
        if (!StringUtil.isEmptyString(customerId)){
            this.customerId = customerId;
            this.recipientInfoVisible = true;
            this.checkboxPayableContact.visible = true;
            this.textboxRecipient.visible = true;
        }
    }

    public setupMultipleCustomerData(customerIds: string[]) {
        if (!Array.isArray(customerIds) || customerIds.length === 0) {
            return;
        }

        this.customerIds = customerIds.map(id => id.trim()).filter(id => !StringUtil.isEmptyString(id));
        if (this.customerIds.length === 1) {
            this.recipientInfoVisible = true;
            this.checkboxPayableContact.visible = true;
            this.textboxRecipient.visible = true;
        }

        this.setupMultipleCustomersDisplayWithModelData();
    }

    private async setupMultipleCustomersDisplayWithModelData() {
        try {
            // Fetch all customer data
            const customerPromises = this.customerIds.map(customerId =>
                new ModelCustomer().searchSingle({ id: customerId }, "lme/ar/Customer")
            );

            const customerRows = await Promise.all(customerPromises);
            const validCustomers = customerRows.filter(row => row != null);

            if (validCustomers.length > 0) {
                // Create lookup model data for multiple customers
                const lookupModelData = validCustomers.map(customerRow => ({
                    "id": customerRow.get("id"),
                    "name": customerRow.get("name")
                }));

                // Set the comma-separated value and the lookup model data
                const customerIdValue = this.customerIds.join(",");
                this.sourceInvoicesDueReminder.searchRow.setValueAndLookupModelData("customer_id", customerIdValue, lookupModelData);
                this.excludabletextboxCustomer.displayData(this.sourceInvoicesDueReminder.searchRow, null, 0);

                // Set customerId to the first one for compatibility with existing recipient logic
                this.customerId = this.customerIds[0];
            }
        } catch (error) {
            console.error("Error setting up multiple customer data:", error);
            // Fallback to just setting the raw value
            const customerIdValue = this.customerIds.join(",");
            this.sourceInvoicesDueReminder.searchRow.set("customer_id", customerIdValue);
            this.excludabletextboxCustomer.displayData(this.sourceInvoicesDueReminder.searchRow, null, 0);
        }
    }

    reportFormatBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.document_type = "C";
        event.filter.template_number = ">0";
    }

    textboxEmailFormatBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.document_type = "X";
        event.filter.template_number = ">0";
    }

    textboxRecipientBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        // For multiple customers, use the first customer ID for recipient lookup
        // This is a reasonable default since recipient lookup typically needs a single parent
        const parentRowId = this.customerIds.length > 0 ? this.customerIds[0] : this.customerId;
        event.filter.parent_row_id = parentRowId;
    }

    checkboxEmailStatementOnChange(){
        const hasCustomers = this.customerIds.length > 0 || !StringUtil.isEmptyString(this.customerId);
        if(this.checkboxEmailStatement.checked && (this.recipientInfoVisible || hasCustomers)){
            this.checkboxPayableContact.enabled = true;
            this.textboxRecipient.enabled = true;
        }
        else {
            this.checkboxPayableContact.enabled = false;
            this.textboxRecipient.enabled = false;
        }
    }

    validateInvoiceDueInTypeOnBlur() {
        const invoiceDueInChecked = this.switchReminderType?.checked
        const value1: number = this.parseStringToInt(this.textboxDueInDays.text)
        const value2: number = this.parseStringToInt(this.textboxDueInToDays.text)
        const validationMsgDueIn = "The first day range field for 'Invoices due in' must be greater than zero." +
                "The second day range field, if not left blanck, must be greater than or equal to the value of the first day range field"
        const validationMsgForPastDueBy = "The first day range field for 'Invoices past due by' must be greater than zero." +
                "The second day range field, if not left blanck, must be greater than or equal to the value of the first day range field"
        if(value1 == null || value1 <1 ) {
            Snackbar.showWarningSnackbar(invoiceDueInChecked? validationMsgForPastDueBy : validationMsgDueIn );
            this.textboxDueInDays.text = "1";
        }
        else if(value2 != null && value1> value2){
            Snackbar.showWarningSnackbar(invoiceDueInChecked? validationMsgForPastDueBy : validationMsgDueIn );
            this.textboxDueInToDays.text = "";
        }
    }

    private parseStringToInt(strValue: string): number | null {
        if (strValue.trim() === "") {
            return null;
        }
        return parseInt(strValue, 10);
    }

    CategoryBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.customer = "Y"
    }

    CustomerBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y", event.filter.type_of = 'C'
    }

    public static showInvoicesDueReminderReportWithDecorator(customerIds: string[]): void {
        const invoicesDueReminderReport = Layout.getLayout("lme/ar/reports/InvoicesDueReminderReport") as InvoicesDueReminderReport;
        invoicesDueReminderReport.addLayoutLoadListener(() => {
            if (customerIds && customerIds.length > 0) {
                invoicesDueReminderReport.setupMultipleCustomerData(customerIds);
            }
            invoicesDueReminderReport.sourceInvoicesDueReminder?.resetDefaultSearchRowToCurrent();
        });

        this.buildInvoicesDueReminderSlideoutTitle(customerIds).then(title => {
            this.showReportInSlideout(invoicesDueReminderReport, title);
        });
    }

    private static showReportInSlideout(reportLayout: Layout, title: string) {
        const reportDecorator = new ReportDecorator({ layout: reportLayout });
        const layoutWrapper = new Layout({
            needsServerLayout: false,
            components: [ reportDecorator ],
            paddingTop: 12,
            paddingRight: 12,
            paddingBottom: 12
        });

        new SlideoutDecorator({
            layout: layoutWrapper,
            width: "95%",
            fillVerticalSpace: true,
            fillHorizontalSpace: true,
            title,
            transitionOptions: {
                direction: Alignment.BOTTOM
            }
        });
    }

    private static async buildInvoicesDueReminderSlideoutTitle(customerIds: string[]): Promise<string> {
        if (!customerIds || customerIds.length === 0) {
            return "Invoices Due Reminder Report";
        }

        if (customerIds.length === 1) {
            try {
                const customerRow = await new ModelCustomer().searchSingle({ id: customerIds[0] }, "lme/ar/Customer");
                if (customerRow) {
                    const customerName = customerRow.get("name");
                    return `Invoices Due Reminder Report - ${customerName}`;
                }
            } catch (error) {
                console.error("Error fetching customer name for title:", error);
            }
            return `Invoices Due Reminder Report - Customer ${customerIds[0]}`;
        } else {
            return `Invoices Due Reminder Report - ${customerIds.length} Customers`;
        }
    }
}
